// Use relative URL in development (proxied by Vite), or environment variable in production
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

class ApiClient {
  /**
   * Builds the Authorization header value based on the host messenger.
   * - Telegram: "tma {initData}" (TG WebApp protocol)
   * - MAX:      "max {initData}" (project convention)
   * - Otherwise: empty string (request will be sent without auth)
   *
   * Telegram has priority if both SDKs happen to expose initData.
   */
  private getAuthHeader(): string {
    const tgInitData = window.Telegram?.WebApp?.initData;
    if (tgInitData) {
      return `tma ${tgInitData}`;
    }
    const maxInitData = window.WebApp?.initData;
    if (maxInitData) {
      return `max ${maxInitData}`;
    }
    return '';
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const authHeader = this.getAuthHeader();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader && { Authorization: authHeader }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: unknown = await response.json();

    // Handle different response formats:
    // Format 1 (documented): { success: true, data: {...} }
    // Format 2 (current backend): { data: {...} }
    // Format 3 (simple): {...}

    // Check for error response
    if (typeof data === 'object' && data !== null && 'success' in data) {
      const dataObj = data as { success?: boolean; error?: { message?: string } };
      if (dataObj.success === false) {
        throw new Error(dataObj.error?.message || 'Unknown error occurred');
      }
    }

    // If response has 'data' field, extract it
    if (typeof data === 'object' && data !== null && 'data' in data) {
      return (data as { data: T }).data;
    }

    // Otherwise return the response as is
    return data as T;
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const authHeader = this.getAuthHeader();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        ...(authHeader && { Authorization: authHeader }),
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: unknown = await response.json();

    // Check for error response
    if (typeof data === 'object' && data !== null && 'success' in data) {
      const dataObj = data as { success?: boolean; error?: { message?: string } };
      if (dataObj.success === false) {
        throw new Error(dataObj.error?.message || 'Unknown error occurred');
      }
    }

    // If response has 'data' field, extract it
    if (typeof data === 'object' && data !== null && 'data' in data) {
      return (data as { data: T }).data;
    }

    // Otherwise return the response as is
    return data as T;
  }
}

export const apiClient = new ApiClient();
